// Card 30, restricted areas (R548, R549; ui-map-interface.md §17.3.30).
//
// A paper chart boxes the water a navy fires into, in a dashed magenta line
// with inward T ticks and a legend that says when the practice ends. This
// piece boxes the water a siege can take: from the moment a tier-2
// structure's vulnerability window is declared, the structure's cell and
// its ring 1 are outlined as one shape and lettered with the hour the
// window opens, or closes once it is open; while the object is contested
// the shape is washed in the attacking faction's colour (R562). The first
// build boxed an open, contested window only, and a reader who never saw
// a box stood before one.
//
// **THE SHAPE IS THE SERVER'S RULE, NOT A DECORATION.** R549 makes a
// structure's cell plus its ring-1 water the apron that follows the
// structure's owner, so the outline this piece traces is exactly the ground
// that flips when the window resolves. `cellsWithin(cell, 1)` is that disc,
// and `unionOutline` is the same trace the territory layer uses, so the line
// falls on the hex edges and on nothing else.
//
// **THE CLOCK IS DERIVED, NEVER READ OFF THE WIRE.** A Sieges frame arrives
// on three edges only, so `SiegeInfo.windowInSec` is a snapshot that can sit
// still for hours. `GameState.windowInSec(hour)` re-derives the figure from
// the world clock, and the piece rebuilds on `'minute'` as well as on
// `'sieges'` so the box goes when the hour is spent.
//
// **THE WASH IS A DISC, NOT A HEX.** The card fills each cell along the hex
// path. The batches lay capsules, rectangles and circles and no polygon, so
// the wash is a disc at the cell's inradius (`fill_r_k`). At `fill_alpha`
// the eye reads a tinted cell either way, and no two discs overlap.

import { chart as S } from '../../../gen/strings';
import { cellsWithin } from '../../../render/hex-ring';
import { HEX_SIZE, cellAt, cellCenter, factionColor, hexCorner } from '../../../world';
import type { FrameInfo } from '../../layer';
import type {
  ChartContext, ChartPack, ChartSinks, ChartTier, Piece, RebuildReason,
} from '../context';
import { CHART_INK, dashedStrokes, onTheHour, outlineStrokes, type Stroke } from './shared';

/** How far past an edge midpoint the neighbour test samples. */
const OUTSIDE_K = 1.2;

/** One outer edge midpoint, and the unit vector that points back inward. */
interface Tick { x: number; y: number; ux: number; uy: number }

/** Everything one contested, open window draws. */
interface Area {
  /** The structure's own cell centre. The legend hangs off it. */
  cx: number;
  cy: number;
  /** The attacking faction's colour, for the wash; −1 while nobody
   *  contests the object, and the shape is not washed. */
  fill: number;
  /** Is the window open now? It picks the legend. */
  open: boolean;
  /** Where the wash discs sit. */
  cells: { x: number; y: number }[];
  strokes: Stroke[];
  ticks: Tick[];
  /** The world hour the legend names, 0..23: the opening hour while the
   *  window is shut, and the closing hour once it is open. */
  hour: number;
}

export class RestrictedPiece implements Piece {
  readonly id = 'restricted' as const;
  readonly tier: ChartTier = 'screen';
  readonly pack: ChartPack = 'static';
  readonly film = 'hide' as const;
  readonly rebuildOn: readonly RebuildReason[] = ['sieges', 'minute', 'reading'];

  private areas: Area[] = [];

  /** The areas, for the piece's own test. */
  get count(): number { return this.areas.length; }

  /** No siege window opens in the settlement: the season is over, so the
   *  box would name an hour that never comes (R2094). */
  visible(ctx: ChartContext): boolean {
    return ctx.state.settlementEndsMs === 0;
  }

  /**
   * Builds one area per declared window (R562).
   *
   * The box says "a fight can take this ground": it stands from the sieges
   * frame that declares the window, names the hour the window opens, and
   * once the window is open names the hour it closes. The wash is the
   * attacker's, and only while the object is contested.
   */
  rebuild(ctx: ChartContext): void {
    this.areas = [];
    for (const s of ctx.state.sieges) {
      const open = ctx.state.windowInSec(s.windowHour) < 0;
      const cells = cellsWithin(s.cell, 1);
      if (cells.length === 0) continue;
      const [cx, cy] = cellCenter(s.cell);
      this.areas.push({
        cx, cy,
        fill: s.contested ? factionColor(s.attacker) : -1,
        open,
        cells: cells.map((c) => {
          const [x, y] = cellCenter(c);
          return { x, y };
        }),
        strokes: outlineStrokes(cells),
        ticks: ticksOf(cells),
        hour: open ? ctx.state.windowClosesHour(s.windowHour) : s.windowHour % 24,
      });
    }
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    if (this.areas.length === 0) return;
    const t = ctx.cfg.pieces.restricted;
    const reading = ctx.reading;
    const modern = reading === 'modern';
    const ink = modern ? CHART_INK.magenta : CHART_INK.pale;
    const dash = t[reading].dash;
    const fillR = HEX_SIZE * t.fig.fill_r_k;
    for (const a of this.areas) {
      if (a.fill >= 0) {
        for (const c of a.cells) {
          sinks.circles.disc(c.x, c.y, fillR, a.fill, t.fig.fill_alpha);
        }
      }
      dashedStrokes(sinks.segments, a.strokes, t.fig.width, ink, t.fig.alpha, dash);
      if (modern) {
        const r = t.modern;
        for (const k of a.ticks) {
          sinks.segments.line(
            k.x, k.y, k.x + k.ux * t.fig.tick_len, k.y + k.uy * t.fig.tick_len,
            r.width, ink, t.fig.alpha,
          );
        }
        const at = onTheHour(a.hour);
        const legend = !a.open ? S.restricted_shut_modern({ at })
          : a.fill >= 0 ? S.restricted_modern({ at }) : S.restricted_open_modern({ at });
        ctx.glyphs.word(
          sinks.quads, legend,
          a.cx + t.fig.legend_dx, a.cy + t.fig.legend_dy,
          'mono', t.text.main, ink, r.alpha, { anchor: 'centre' },
        );
        continue;
      }
      // The historical chart writes the word across the water instead of
      // ticking the line, and says the hour as hh:00 (R1544).
      const r = t.historical;
      // The word and the sentence are one block, so they grow about one
      // pivot, the middle of the sentence.
      const pivot = { x: a.cx + t.fig.legend_dx, y: a.cy + t.fig.legend_dy };
      ctx.glyphs.word(
        sinks.quads, S.restricted_word_historical,
        a.cx + t.fig.legend_dx, a.cy + r.fig.word_dy,
        'fell', t.text.word, ink, r.fig.word_alpha, { anchor: 'centre', pivot },
      );
      const hour = onTheHour(a.hour);
      ctx.glyphs.word(
        sinks.quads,
        a.open ? S.restricted_historical({ hour }) : S.restricted_shut_historical({ hour }),
        a.cx + t.fig.legend_dx, a.cy + t.fig.legend_dy,
        'fell', t.text.main, ink, r.fig.sentence_alpha, { anchor: 'centre', pivot },
      );
    }
  }
}

/**
 * One tick at the midpoint of every hex edge the set does not share.
 *
 * The midpoint is the mean of two corners of the cell, and the neighbour
 * across it is looked up by sampling a point just outside that midpoint.
 * Nothing here depends on how the six neighbours are numbered, so the ticks
 * cannot fall out of step with the outline.
 */
function ticksOf(cells: readonly number[]): Tick[] {
  const set = new Set(cells);
  const out: Tick[] = [];
  for (const cell of cells) {
    const [cx, cy] = cellCenter(cell);
    for (let k = 0; k < 6; k++) {
      const [ax, ay] = hexCorner(k);
      const [bx, by] = hexCorner((k + 1) % 6);
      const mx = (ax + bx) / 2, my = (ay + by) / 2;
      const nb = cellAt(cx + mx * OUTSIDE_K, cy + my * OUTSIDE_K);
      if (nb >= 0 && set.has(nb)) continue;
      const d = Math.hypot(mx, my) || 1;
      out.push({ x: cx + mx, y: cy + my, ux: -mx / d, uy: -my / d });
    }
  }
  return out;
}
